function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

export const ELITE_BATCH23_PART10: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ─── 154. Personal Wiki Entry Promoter ────────────────────────────────

  "Personal Wiki Entry Promoter": {
    nodes: [
      trigger(
        "Notion webhook: Page shared publicly or with team",
        "webhook_app",
        {
          source: "notion",
          event: "page.shared",
          howItStarts:
            "Fires when a Notion page is shared beyond its creator, indicating the author wants to promote personal knowledge to the team wiki. Provides page ID, title, content, and sharing scope.",
          setup:
            "Configure a Notion integration with webhook support on the target workspace. Subscribe to page sharing events for wiki databases.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Extract knowledge content and classify topic",
        280,
        "You are a knowledge management specialist. Extract the article title, summary, key topics, target audience, content type (how-to/reference/decision/process), and any tags from the Notion page. Return title, summary, topics, audience, content_type, tags, quality_score.",
        "Analyze this Notion wiki page for promotion to the team knowledge base: {webhook_payload}",
        [
          { field: "title", as: "article_title", type: "string" },
          { field: "summary", as: "article_summary", type: "string" },
          { field: "topics", as: "topics", type: "string" },
          { field: "audience", as: "target_audience", type: "string" },
          { field: "content_type", as: "content_type", type: "string" },
          { field: "tags", as: "article_tags", type: "string" },
          { field: "quality_score", as: "quality_score", type: "number" },
        ],
      ),
      n("3", "runCode", "Check for duplicate content in Confluence", 520, {
        runCodeDetails: {
          code: "const title = inputs.article_title; const searchQuery = title.toLowerCase().replace(/[^a-z0-9 ]/g, '').split(' ').slice(0, 5).join(' '); output = { search_query: searchQuery, is_quality: inputs.quality_score >= 60, promote_ready: inputs.quality_score >= 60 };",
          outputs: [
            { name: "search_query", type: "string" },
            { name: "is_quality", type: "boolean" },
            { name: "promote_ready", type: "boolean" },
          ],
        },
      }),
      n(
        "4",
        "condition",
        "Check: Content quality sufficient for promotion?",
        760,
        {
          conditionDetails: {
            conditions: [
              { variable: "{is_quality}", operator: "==", value: "true" },
            ],
          },
        },
      ),
      n("5", "hitApi", "Confluence: Create knowledge base article", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://{confluence_domain}.atlassian.net/wiki/rest/api/content",
          headers: [
            { key: "Authorization", value: "Basic {confluence_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "type", value: "page" },
            { key: "title", value: "{article_title}" },
            { key: "space", value: '{"key":"{kb_space_key}"}' },
            {
              key: "body",
              value:
                '{"storage":{"value":"{article_summary}","representation":"storage"}}',
            },
            {
              key: "metadata",
              value:
                '{"labels":[{"name":"{content_type}"},{"name":"{article_tags}"}]}',
            },
          ],
          variables: [
            { name: "id", customName: "confluence_page_id", type: "string" },
            {
              name: "_links.webui",
              customName: "confluence_url",
              type: "string",
            },
          ],
        },
      }),
      slackNode(
        "6",
        "Slack: Notify knowledge team of new wiki article",
        1240,
        ":book: *New Knowledge Base Article*\n*{article_title}*\nType: {content_type} | Topics: {topics}\nAudience: {target_audience}\nQuality Score: {quality_score}/100\n\nConfluence: {confluence_url}\nPromoted from Notion by: {author_name}",
      ),
      sheetsNode(
        "7",
        "Google Sheets: Log knowledge promotion event",
        1480,
        "KnowledgePromotion",
        '[["{today_date}","{article_title}","{content_type}","{topics}","{quality_score}","{confluence_page_id}","{author_name}"]]',
      ),
      n("8", "runCode", "Tag article as searchable and update index", 1720, {
        runCodeDetails: {
          code: "const tags = inputs.article_tags.split(',').map(t => t.trim()); const searchTerms = [inputs.article_title, ...tags, inputs.topics].join(' ').toLowerCase(); output = { search_index_entry: searchTerms, tag_count: tags.length, indexed: true };",
          outputs: [
            { name: "search_index_entry", type: "string" },
            { name: "tag_count", type: "number" },
            { name: "indexed", type: "boolean" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Detects shared Notion pages, uses AI to extract knowledge content and assess quality, checks for duplicates, creates a Confluence knowledge base article for quality content, notifies the knowledge team via Slack, logs the promotion event, and indexes the article for searchability.",
  },

  // ─── 155. Action-Item Reminder Bot ────────────────────────────────────

  "Action-Item Reminder Bot": {
    nodes: [
      trigger(
        "Schedule: Check for overdue action items daily at 9 AM",
        "schedule",
        {
          schedule: "Every day at 9:00 AM",
          howItStarts:
            "Fires daily at 9 AM to check Asana for overdue tasks, group them by owner, and send targeted reminders via Slack DM with escalation for severely overdue items.",
          setup:
            "Configure the Asana project IDs to monitor. Set the overdue threshold for manager escalation (default: 3 days).",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "Asana: Fetch all overdue tasks from tracked projects",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://app.asana.com/api/1.0/tasks?project={project_id}&completed=false&opt_fields=name,assignee.name,assignee.email,due_on,custom_fields,notes&due_before={today_date}",
            headers: [{ key: "Authorization", value: "Bearer {asana_token}" }],
            body: [],
            variables: [
              { name: "data", customName: "overdue_tasks", type: "object" },
              {
                name: "data.length",
                customName: "overdue_count",
                type: "number",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "runCode",
        "Group overdue tasks by owner and compute days overdue",
        520,
        {
          runCodeDetails: {
            code: "const tasks = inputs.overdue_tasks || []; const today = new Date(); const byOwner = {}; tasks.forEach(t => { const owner = t.assignee?.name || 'Unassigned'; const ownerEmail = t.assignee?.email || ''; const due = new Date(t.due_on); const daysOver = Math.ceil((today - due) / (1000 * 60 * 60 * 24)); if (!byOwner[owner]) byOwner[owner] = { email: ownerEmail, tasks: [] }; byOwner[owner].tasks.push({ name: t.name, days_overdue: daysOver, due_on: t.due_on }); }); const severelyOverdue = tasks.filter(t => { const days = Math.ceil((today - new Date(t.due_on)) / 86400000); return days >= 3; }); output = { grouped_tasks: JSON.stringify(byOwner), owner_count: Object.keys(byOwner).length, severely_overdue_count: severelyOverdue.length, needs_escalation: severelyOverdue.length > 0 };",
            outputs: [
              { name: "grouped_tasks", type: "string" },
              { name: "owner_count", type: "number" },
              { name: "severely_overdue_count", type: "number" },
              { name: "needs_escalation", type: "boolean" },
            ],
          },
        },
      ),
      slackNode(
        "4",
        "Slack: DM each owner with their overdue items",
        760,
        ":alarm_clock: *Overdue Action Items Reminder*\n\nHi {owner_name}, you have overdue tasks that need attention:\n\n{owner_task_list}\n\nPlease update or complete these items today. If you need to adjust deadlines, update them in Asana.",
      ),
      n(
        "5",
        "condition",
        "Check: Any items 3+ days overdue requiring escalation?",
        1000,
        {
          conditionDetails: {
            conditions: [
              { variable: "{needs_escalation}", operator: "==", value: "true" },
            ],
          },
        },
      ),
      slackNode(
        "6",
        "Slack: Alert manager of severely overdue items",
        1240,
        ":warning: *Overdue Task Escalation*\n\n{severely_overdue_count} tasks are 3+ days overdue across {owner_count} team members.\n\nSeverely overdue items require management attention. Please follow up with the assigned owners.\n\nFull report available in the tracking sheet.",
      ),
      emailNode(
        "7",
        "Email: Send escalation report to manager",
        1480,
        "{manager_email}",
        "Overdue Task Escalation — {severely_overdue_count} items 3+ days late",
        "Hi,\n\n{severely_overdue_count} action items are 3+ days overdue and require your attention:\n\nTotal overdue: {overdue_count}\nSeverely overdue (3+ days): {severely_overdue_count}\nAffected team members: {owner_count}\n\nGrouped by owner:\n{grouped_tasks}\n\nPlease follow up with the assigned owners to unblock or reprioritize these items.",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Track overdue completion trends",
        1720,
        "OverdueTracking",
        '[["{today_date}","{overdue_count}","{severely_overdue_count}","{owner_count}","{needs_escalation}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Checks Asana daily for overdue tasks, groups them by owner with days-overdue counts, DMs each owner via Slack, escalates to managers when items are 3+ days overdue via Slack and email, and logs overdue trends to Google Sheets for tracking team delivery patterns.",
  },

  // ─── 156. Team Recognition from Survey Comments ───────────────────────

  "Team Recognition from Survey Comments": {
    nodes: [
      trigger(
        "Outgrow Forms webhook: Positive team mention in survey response",
        "webhook_app",
        {
          source: "outgrow-forms",
          event: "form_response",
          howItStarts:
            "Fires when a Outgrow Forms survey response is submitted that contains a positive mention of a team member, providing the response text, respondent info, and form metadata.",
          setup:
            "Configure a Outgrow Forms webhook for the employee recognition or engagement survey. Filter for responses with positive sentiment in open-text fields.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Extract recognized person and recognition details",
        280,
        "You are an HR recognition analyst. Extract the recognized person's name, the specific behavior or achievement being recognized, the category (teamwork/leadership/innovation/customer-focus/going-above), the recognition quote, and the sentiment intensity (1-10). Return recognized_name, achievement, category, quote, intensity, recognizer_name.",
        "Extract recognition details from this survey response: {webhook_payload}",
        [
          { field: "recognized_name", as: "recognized_name", type: "string" },
          { field: "achievement", as: "achievement_desc", type: "string" },
          { field: "category", as: "recognition_category", type: "string" },
          { field: "quote", as: "recognition_quote", type: "string" },
          { field: "intensity", as: "sentiment_intensity", type: "number" },
          { field: "recognizer_name", as: "recognizer_name", type: "string" },
        ],
      ),
      n("3", "runCode", "Validate employee and format recognition post", 520, {
        runCodeDetails: {
          code: "const emojis = { 'teamwork': ':handshake:', 'leadership': ':star2:', 'innovation': ':bulb:', 'customer-focus': ':heart:', 'going-above': ':rocket:' }; const emoji = emojis[inputs.recognition_category.toLowerCase()] || ':trophy:'; const isHighlight = inputs.sentiment_intensity >= 8; output = { category_emoji: emoji, is_highlight: isHighlight, formatted_recognition: emoji + ' ' + inputs.recognized_name + ' recognized for ' + inputs.recognition_category };",
          outputs: [
            { name: "category_emoji", type: "string" },
            { name: "is_highlight", type: "boolean" },
            { name: "formatted_recognition", type: "string" },
          ],
        },
      }),
      slackNode(
        "4",
        "Slack: Post recognition to #kudos channel",
        760,
        "{category_emoji} *Kudos to {recognized_name}!* {category_emoji}\n\n_{recognition_quote}_\n\n*Category:* {recognition_category}\n*Recognized for:* {achievement_desc}\n*From:* {recognizer_name}\n\nKeep up the amazing work! :clap:",
      ),
      n(
        "5",
        "condition",
        "Check: Is this a manager-level recognition highlight?",
        1000,
        {
          conditionDetails: {
            conditions: [
              { variable: "{is_highlight}", operator: "==", value: "true" },
            ],
          },
        },
      ),
      emailNode(
        "6",
        "Email: Notify HR of outstanding recognition for records",
        1240,
        "{hr_email}",
        "Outstanding Recognition: {recognized_name} — {recognition_category}",
        "Hi HR team,\n\nAn outstanding recognition has been received:\n\nEmployee: {recognized_name}\nCategory: {recognition_category}\nAchievement: {achievement_desc}\nRecognition Quote: {recognition_quote}\nRecognizer: {recognizer_name}\nSentiment Intensity: {sentiment_intensity}/10\n\nThis is a highlight-level recognition and may warrant inclusion in the quarterly awards or newsletter.",
      ),
      sheetsNode(
        "7",
        "Google Sheets: Log recognition for quarterly reporting",
        1480,
        "Recognition",
        '[["{today_date}","{recognized_name}","{recognition_category}","{achievement_desc}","{recognizer_name}","{sentiment_intensity}","{is_highlight}"]]',
      ),
      n(
        "8",
        "hitApi",
        "BambooHR: Add recognition note to employee profile",
        1720,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.bamboohr.com/api/gateway.php/{company_domain}/v1/employees/{employee_id}/notes",
            headers: [
              { key: "Authorization", value: "Basic {bamboohr_auth}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "note",
                value:
                  "Recognition ({recognition_category}): {achievement_desc}. Quote: {recognition_quote}. From: {recognizer_name}. Date: {today_date}",
              },
            ],
            variables: [{ name: "id", customName: "note_id", type: "string" }],
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Captures positive team mentions from Outgrow Forms survey responses, uses AI to extract recognition details and categorize, posts a formatted kudos to Slack, notifies HR for outstanding recognitions, logs to Google Sheets for quarterly reporting, and adds a note to the employee's BambooHR profile.",
  },

  // ─── 157. Decision Log Updater ────────────────────────────────────────

  "Decision Log Updater": {
    nodes: [
      trigger(
        "Slack webhook: Decision thread tagged with :decision: emoji",
        "webhook_app",
        {
          source: "slack",
          event: "reaction_added",
          howItStarts:
            "Fires when the :decision: emoji is added to a Slack message thread, signaling that the thread contains a finalized team decision that should be logged.",
          setup:
            "Configure Slack event subscriptions for reaction_added. Filter for the :decision: emoji. Ensure the bot has access to read thread history.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Extract decision, context, and rationale from thread",
        280,
        "You are a decision documentation specialist. Extract the decision statement, context/background, rationale, alternatives considered, decision maker(s), stakeholders consulted, effective date, and any reversibility notes. Return decision, context, rationale, alternatives, decision_makers, stakeholders, effective_date, reversible.",
        "Extract and document this decision from the Slack thread: {webhook_payload}",
        [
          { field: "decision", as: "decision_statement", type: "string" },
          { field: "context", as: "decision_context", type: "string" },
          { field: "rationale", as: "rationale", type: "string" },
          {
            field: "alternatives",
            as: "alternatives_considered",
            type: "string",
          },
          { field: "decision_makers", as: "decision_makers", type: "string" },
          { field: "stakeholders", as: "stakeholders", type: "string" },
          { field: "reversible", as: "is_reversible", type: "string" },
        ],
      ),
      n("3", "runCode", "Validate completeness and assign decision ID", 520, {
        runCodeDetails: {
          code: "const hasDecision = inputs.decision_statement && inputs.decision_statement.length > 10; const hasRationale = inputs.rationale && inputs.rationale.length > 10; const complete = hasDecision && hasRationale; const decisionId = 'DEC-' + Date.now().toString(36).toUpperCase(); output = { decision_id: decisionId, is_complete: complete, missing_fields: !hasDecision ? 'decision statement' : !hasRationale ? 'rationale' : 'none' };",
          outputs: [
            { name: "decision_id", type: "string" },
            { name: "is_complete", type: "boolean" },
            { name: "missing_fields", type: "string" },
          ],
        },
      }),
      n(
        "4",
        "hitApi",
        "Notion: Create decision log entry with full context",
        760,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.notion.com/v1/pages",
            headers: [
              { key: "Authorization", value: "Bearer {notion_token}" },
              { key: "Content-Type", value: "application/json" },
              { key: "Notion-Version", value: "2022-06-28" },
            ],
            body: [
              {
                key: "parent",
                value: '{"database_id":"{decision_log_db_id}"}',
              },
              {
                key: "properties",
                value:
                  '{"Decision ID":{"title":[{"text":{"content":"{decision_id}: {decision_statement}"}}]},"Context":{"rich_text":[{"text":{"content":"{decision_context}"}}]},"Rationale":{"rich_text":[{"text":{"content":"{rationale}"}}]},"Alternatives":{"rich_text":[{"text":{"content":"{alternatives_considered}"}}]},"Decision Makers":{"rich_text":[{"text":{"content":"{decision_makers}"}}]},"Reversible":{"select":{"name":"{is_reversible}"}},"Date":{"date":{"start":"{today_date}"}}}',
              },
            ],
            variables: [
              { name: "id", customName: "notion_page_id", type: "string" },
              { name: "url", customName: "notion_page_url", type: "string" },
            ],
          },
        },
      ),
      slackNode(
        "5",
        "Slack: Confirm decision logged in thread",
        1000,
        ":white_check_mark: *Decision Logged*\nID: {decision_id}\n\n*Decision:* {decision_statement}\n*Rationale:* {rationale}\n*Decided by:* {decision_makers}\n\nFull record: {notion_page_url}",
      ),
      emailNode(
        "6",
        "Email: Notify stakeholders of documented decision",
        1240,
        "{stakeholder_emails}",
        "Decision Recorded: {decision_id} — {decision_statement}",
        "Hi,\n\nA decision affecting your area has been documented:\n\nDecision ID: {decision_id}\nDecision: {decision_statement}\n\nContext: {decision_context}\n\nRationale: {rationale}\n\nAlternatives Considered: {alternatives_considered}\n\nDecision Makers: {decision_makers}\nReversible: {is_reversible}\n\nFull record: {notion_page_url}",
      ),
      sheetsNode(
        "7",
        "Google Sheets: Log decision for tracking and search",
        1480,
        "DecisionLog",
        '[["{today_date}","{decision_id}","{decision_statement}","{decision_makers}","{is_reversible}","{notion_page_id}"]]',
      ),
      n("8", "runCode", "Link decision to related project in Notion", 1720, {
        runCodeDetails: {
          code: "const keywords = inputs.decision_statement.toLowerCase().split(' ').filter(w => w.length > 4).slice(0, 5); output = { search_keywords: keywords.join(', '), linked: true, decision_archived: true };",
          outputs: [
            { name: "search_keywords", type: "string" },
            { name: "linked", type: "boolean" },
            { name: "decision_archived", type: "boolean" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Captures decisions tagged with the :decision: emoji in Slack, uses AI to extract the full decision context and rationale, creates a structured Notion decision log entry with a unique ID, confirms in the Slack thread, emails stakeholders, logs to Google Sheets, and links to related projects.",
  },

  // ─── 158. Shared Inbox Triage to Tasks ────────────────────────────────

  "Shared Inbox Triage to Tasks": {
    nodes: [
      trigger("Email trigger: New message in shared team inbox", "email", {
        howItStarts:
          "Fires when a new email arrives in the shared team inbox (e.g., team@company.com), providing the sender, subject, body, attachments, and receipt timestamp.",
        setup:
          "Configure email forwarding or IMAP integration for the shared inbox. Set up filters to exclude automated notifications and out-of-office replies.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Classify intent and urgency from email content",
        280,
        "You are an email triage specialist. Classify the email by intent (request/question/complaint/information/spam), urgency (critical/high/medium/low), department (engineering/sales/support/ops/hr), required action, and extract a concise summary. Return intent, urgency, department, action_required, summary, sender_name, sender_email, subject.",
        "Classify and triage this shared inbox email: Subject: {email_subject}. From: {email_from}. Body: {email_body}",
        [
          { field: "intent", as: "email_intent", type: "string" },
          { field: "urgency", as: "urgency_level", type: "string" },
          { field: "department", as: "target_department", type: "string" },
          { field: "action_required", as: "action_required", type: "string" },
          { field: "summary", as: "email_summary", type: "string" },
          { field: "sender_name", as: "sender_name", type: "string" },
        ],
      ),
      n("3", "runCode", "Route to appropriate team and set priority", 520, {
        runCodeDetails: {
          code: "const deptMap = { 'engineering': '{eng_project_id}', 'sales': '{sales_project_id}', 'support': '{support_project_id}', 'ops': '{ops_project_id}', 'hr': '{hr_project_id}' }; const dept = inputs.target_department.toLowerCase(); const projectId = deptMap[dept] || deptMap.ops; const isUrgent = inputs.urgency_level === 'critical' || inputs.urgency_level === 'high'; const dueDate = isUrgent ? new Date(Date.now() + 86400000).toISOString().slice(0, 10) : new Date(Date.now() + 3 * 86400000).toISOString().slice(0, 10); output = { asana_project_id: projectId, is_urgent: isUrgent, due_date: dueDate, routing: dept };",
          outputs: [
            { name: "asana_project_id", type: "string" },
            { name: "is_urgent", type: "boolean" },
            { name: "due_date", type: "string" },
            { name: "routing", type: "string" },
          ],
        },
      }),
      n("4", "hitApi", "Asana: Create task from triaged email", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"[{email_intent}] {email_subject}","notes":"From: {sender_name}\\nSummary: {email_summary}\\nAction Required: {action_required}\\nUrgency: {urgency_level}\\nDepartment: {target_department}","projects":["{asana_project_id}"],"due_on":"{due_date}"}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "asana_task_id", type: "string" },
            {
              name: "data.permalink_url",
              customName: "asana_task_url",
              type: "string",
            },
          ],
        },
      }),
      n("5", "condition", "Check: Is this email urgent?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "{is_urgent}", operator: "==", value: "true" },
          ],
        },
      }),
      slackNode(
        "6",
        "Slack: Alert team of urgent email requiring action",
        1240,
        ":rotating_light: *Urgent Shared Inbox Email*\n*Subject:* {email_subject}\n*From:* {sender_name}\n*Intent:* {email_intent} | *Urgency:* {urgency_level}\n*Department:* {target_department}\n\nSummary: {email_summary}\nAction: {action_required}\n\nAsana: {asana_task_url}",
      ),
      emailNode(
        "7",
        "Email: Send auto-reply acknowledging receipt",
        1480,
        "{sender_email}",
        "Re: {email_subject} — Received and Assigned",
        "Hi {sender_name},\n\nThank you for contacting us. Your message has been received and routed to our {target_department} team.\n\nReference: {asana_task_id}\nPriority: {urgency_level}\nExpected response: {due_date}\n\nIf this is urgent, please reply to this email with 'URGENT' in the subject line.\n\nBest regards,\nTeam Support",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log email triage for reporting",
        1720,
        "InboxTriage",
        '[["{today_date}","{sender_name}","{email_subject}","{email_intent}","{urgency_level}","{target_department}","{asana_task_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Triages shared inbox emails using AI to classify intent and urgency, routes to the appropriate department, creates an Asana task with priority-based due dates, alerts via Slack for urgent items, sends an auto-reply to the sender with a reference number, and logs triage data to Google Sheets.",
  },

  // ─── 159. Incident War-Room Setup ─────────────────────────────────────

  "Incident War-Room Setup": {
    nodes: [
      trigger("PagerDuty webhook: P1 incident triggered", "webhook_app", {
        source: "pagerduty",
        event: "incident.trigger",
        howItStarts:
          "Fires when a P1 (critical) incident is triggered in PagerDuty, providing the incident ID, title, service affected, urgency, and triggered-by details.",
        setup:
          "Configure a PagerDuty webhook for incident.trigger events filtered by high urgency. Ensure the integration has incident read access.",
        inputFields: [],
      }),
      n("2", "hitApi", "Slack: Create dedicated incident channel", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://slack.com/api/conversations.create",
          headers: [
            { key: "Authorization", value: "Bearer {slack_bot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "inc-{incident_id}-{short_title}" },
            { key: "is_private", value: "false" },
          ],
          variables: [
            {
              name: "channel.id",
              customName: "incident_channel_id",
              type: "string",
            },
            {
              name: "channel.name",
              customName: "incident_channel_name",
              type: "string",
            },
          ],
        },
      }),
      n(
        "3",
        "hitApi",
        "Slack: Invite on-call responders to incident channel",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://slack.com/api/conversations.invite",
            headers: [
              { key: "Authorization", value: "Bearer {slack_bot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "channel", value: "{incident_channel_id}" },
              { key: "users", value: "{oncall_user_ids}" },
            ],
            variables: [
              { name: "ok", customName: "invite_success", type: "boolean" },
            ],
          },
        },
      ),
      n("4", "hitApi", "Zoom: Create war-room video meeting", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.zoom.us/v2/users/{zoom_host_id}/meetings",
          headers: [
            { key: "Authorization", value: "Bearer {zoom_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "topic", value: "Incident War Room: {incident_title}" },
            { key: "type", value: "1" },
            {
              key: "settings",
              value: '{"join_before_host":true,"waiting_room":false}',
            },
          ],
          variables: [
            { name: "id", customName: "zoom_meeting_id", type: "string" },
            { name: "join_url", customName: "zoom_join_url", type: "string" },
          ],
        },
      }),
      slackNode(
        "5",
        "Slack: Post incident summary and war-room links",
        1000,
        ":rotating_light: *P1 INCIDENT — WAR ROOM ACTIVE* :rotating_light:\n\n*Incident:* {incident_title}\n*ID:* {incident_id}\n*Service:* {affected_service}\n*Triggered:* {incident_time}\n\n:video_camera: *Zoom War Room:* {zoom_join_url}\n:slack: *Channel:* #{incident_channel_name}\n\n*Roles needed:*\n- Incident Commander\n- Technical Lead\n- Communications Lead\n\nPlease join immediately.",
      ),
      n("6", "hitApi", "Jira: Create incident ticket with timeline", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/issue",
          headers: [
            { key: "Authorization", value: "Basic {jira_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "fields",
              value:
                '{"project":{"key":"{incidents_project_key}"},"issuetype":{"name":"Bug"},"summary":"[P1] {incident_title}","description":{"type":"doc","version":1,"content":[{"type":"paragraph","content":[{"type":"text","text":"PagerDuty ID: {incident_id}\\nService: {affected_service}\\nTriggered: {incident_time}\\nWar Room: {zoom_join_url}\\nSlack: #{incident_channel_name}"}]}]},"priority":{"name":"Highest"}}',
            },
          ],
          variables: [
            { name: "key", customName: "jira_incident_key", type: "string" },
          ],
        },
      }),
      emailNode(
        "7",
        "Email: Notify stakeholders of active P1 incident",
        1480,
        "{stakeholder_emails}",
        "P1 Incident Active: {incident_title}",
        "A P1 incident is in progress:\n\nIncident: {incident_title}\nService: {affected_service}\nPagerDuty ID: {incident_id}\nJira: {jira_incident_key}\n\nWar Room:\n- Zoom: {zoom_join_url}\n- Slack: #{incident_channel_name}\n\nThe incident team has been assembled. Updates will follow in the Slack channel and via email.",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log incident timeline start",
        1720,
        "IncidentTimeline",
        '[["{incident_time}","{incident_id}","{incident_title}","{affected_service}","{jira_incident_key}","{zoom_meeting_id}","{incident_channel_name}","ACTIVE"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Automates P1 incident response by creating a dedicated Slack channel, inviting on-call responders, spinning up a Zoom war-room meeting, posting all links and role assignments, creating a Jira incident ticket, emailing stakeholders, and logging the timeline to Google Sheets.",
  },

  // ─── 160. Approval Workflow for Documents ─────────────────────────────

  "Approval Workflow for Documents": {
    nodes: [
      trigger(
        "Google Drive webhook: Document marked ready for review",
        "webhook_app",
        {
          source: "google_drive",
          event: "file.modified",
          howItStarts:
            "Fires when a document in the review folder is updated or has its metadata changed to 'Ready for Review', providing the file ID, name, owner, and modification time.",
          setup:
            "Set up Google Drive push notifications for the review folder. Filter for files with status property set to 'Ready for Review' or moved to the review folder.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Extract document metadata and determine review type",
        280,
        "You are a document workflow analyst. Extract the document title, type (contract/proposal/policy/report/spec), owner, department, version, and determine the appropriate review path (single approver, sequential, parallel). Return doc_title, doc_type, owner_name, owner_email, department, version, review_path, approver_count.",
        "Analyze this document and determine the approval workflow: {webhook_payload}",
        [
          { field: "doc_title", as: "doc_title", type: "string" },
          { field: "doc_type", as: "doc_type", type: "string" },
          { field: "owner_name", as: "doc_owner", type: "string" },
          { field: "owner_email", as: "owner_email", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "review_path", as: "review_path", type: "string" },
          { field: "approver_count", as: "approver_count", type: "number" },
        ],
      ),
      n(
        "3",
        "runCode",
        "Determine approvers based on document type and department",
        520,
        {
          runCodeDetails: {
            code: "const approverMap = { 'contract': ['legal@company.com', 'finance@company.com'], 'proposal': ['sales-lead@company.com'], 'policy': ['hr-lead@company.com', 'legal@company.com'], 'report': ['dept-head@company.com'], 'spec': ['eng-lead@company.com'] }; const type = inputs.doc_type.toLowerCase(); const approvers = approverMap[type] || ['manager@company.com']; output = { approver_emails: approvers.join(','), primary_approver: approvers[0], needs_multiple: approvers.length > 1 };",
            outputs: [
              { name: "approver_emails", type: "string" },
              { name: "primary_approver", type: "string" },
              { name: "needs_multiple", type: "boolean" },
            ],
          },
        },
      ),
      emailNode(
        "4",
        "Email: Request approval from designated reviewers",
        760,
        "{approver_emails}",
        "Document Review Requested: {doc_title}",
        "Hi,\n\nA document requires your review and approval:\n\nDocument: {doc_title}\nType: {doc_type}\nOwner: {doc_owner} ({department})\nVersion: {version}\nReview Path: {review_path}\n\nPlease review the document in Google Drive: https://drive.google.com/file/d/{file_id}\n\nApprove or request changes by replying to this email or using the approval form.",
      ),
      n(
        "5",
        "approvalGate",
        "Reviewer: Approve or request changes on document",
        1000,
        {
          approvalGateDetails: {
            approvers: ["{primary_approver}"],
            message:
              "Document '{doc_title}' ({doc_type}) from {doc_owner} requires your approval. Review at: https://drive.google.com/file/d/{file_id}",
          },
        },
      ),
      n(
        "6",
        "hitApi",
        "Google Drive: Update document permissions and status",
        1240,
        {
          hitApiDetails: {
            method: "PATCH",
            url: "https://www.googleapis.com/drive/v3/files/{file_id}",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"approval_status":"approved","approved_by":"{primary_approver}","approved_date":"{today_date}"}',
              },
            ],
            variables: [
              { name: "id", customName: "updated_file_id", type: "string" },
            ],
          },
        },
      ),
      slackNode(
        "7",
        "Slack: Notify team of document approval status",
        1480,
        ":white_check_mark: *Document Approved*\n*{doc_title}* ({doc_type})\nOwner: {doc_owner}\nApproved by: {primary_approver}\nDate: {today_date}\n\nDocument: https://drive.google.com/file/d/{file_id}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log approval for audit trail",
        1720,
        "DocApprovals",
        '[["{today_date}","{doc_title}","{doc_type}","{doc_owner}","{department}","{primary_approver}","{review_path}","approved"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Manages document approval workflows by detecting review-ready documents in Google Drive, using AI to classify document type and determine the review path, emailing approvers, gating on approval, updating Drive metadata, notifying via Slack, and maintaining an audit log in Google Sheets.",
  },

  // ─── 161. Contract Review Request Routing ─────────────────────────────

  "Contract Review Request Routing": {
    nodes: [
      trigger(
        "Webhook form: Contract review request submitted",
        "webhook_form",
        {
          howItStarts:
            "Fires when a team member submits a contract review request form, providing the contract type, value, counterparty, urgency, and requester details.",
          setup:
            "Create a contract review request form with fields for contract type, estimated value, counterparty name, urgency level, and any special terms or notes.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Extract contract details and classify complexity",
        280,
        "You are a legal operations analyst. Extract the contract type (NDA/MSA/SOW/SaaS/vendor/employment), estimated value, counterparty, special terms, urgency, requester name and email. Classify the review complexity (simple/standard/complex). Return contract_type, value, counterparty, special_terms, urgency, requester_name, requester_email, complexity.",
        "Analyze this contract review request and classify it: {webhook_payload}",
        [
          { field: "contract_type", as: "contract_type", type: "string" },
          { field: "value", as: "contract_value", type: "string" },
          { field: "counterparty", as: "counterparty", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "complexity", as: "review_complexity", type: "string" },
        ],
      ),
      n("3", "runCode", "Route by contract type and value threshold", 520, {
        runCodeDetails: {
          code: "const value = parseFloat(inputs.contract_value.replace(/[^0-9.]/g, '')) || 0; const isHighValue = value > 50000; const needsFinance = isHighValue || inputs.contract_type === 'vendor' || inputs.contract_type === 'SOW'; const slaHours = inputs.urgency === 'critical' ? 24 : inputs.review_complexity === 'complex' ? 120 : 72; output = { is_high_value: isHighValue, needs_finance_review: needsFinance, sla_hours: slaHours, parsed_value: value };",
          outputs: [
            { name: "is_high_value", type: "boolean" },
            { name: "needs_finance_review", type: "boolean" },
            { name: "sla_hours", type: "number" },
            { name: "parsed_value", type: "number" },
          ],
        },
      }),
      n("4", "condition", "Check: Contract value exceeds $50K?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "{is_high_value}", operator: "==", value: "true" },
          ],
        },
      }),
      n(
        "5",
        "approvalGate",
        "Legal + Finance: Review high-value contract",
        1000,
        {
          approvalGateDetails: {
            approvers: ["{legal_lead_email}", "{finance_lead_email}"],
            message:
              "High-value contract review required:\n\nType: {contract_type}\nValue: {contract_value}\nCounterparty: {counterparty}\nComplexity: {review_complexity}\nSLA: {sla_hours} hours\n\nRequested by: {requester_name}",
          },
        },
      ),
      slackNode(
        "6",
        "Slack: Notify legal team of contract review request",
        1240,
        ":page_facing_up: *Contract Review Request*\n*Type:* {contract_type} | *Value:* {contract_value}\n*Counterparty:* {counterparty}\n*Complexity:* {review_complexity} | *Urgency:* {urgency}\n*Requester:* {requester_name}\n*SLA:* {sla_hours} hours\n*Finance Review:* {needs_finance_review}",
      ),
      emailNode(
        "7",
        "Email: Confirm review request to requester",
        1480,
        "{requester_email}",
        "Contract Review Request Received — {contract_type} with {counterparty}",
        "Hi {requester_name},\n\nYour contract review request has been received and routed:\n\nContract Type: {contract_type}\nCounterparty: {counterparty}\nEstimated Value: {contract_value}\nReview Complexity: {review_complexity}\n\nExpected SLA: {sla_hours} hours\nFinance Review Required: {needs_finance_review}\n\nThe legal team will reach out if they need additional information.",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log contract review for tracking",
        1720,
        "ContractReviews",
        '[["{today_date}","{contract_type}","{counterparty}","{contract_value}","{review_complexity}","{urgency}","{sla_hours}","{requester_name}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Routes contract review requests by extracting details with AI, classifying complexity, routing high-value contracts (over $50K) through a combined legal and finance approval gate, notifying the legal team via Slack, confirming receipt to the requester, and logging reviews with SLA tracking.",
  },

  // ─── 162. Knowledge Base Update Request ───────────────────────────────

  "Knowledge Base Update Request": {
    nodes: [
      trigger(
        "Slack webhook: KB update request tagged in channel",
        "webhook_app",
        {
          source: "slack",
          event: "reaction_added",
          howItStarts:
            "Fires when the :book: or :pencil: emoji is added to a Slack message requesting a knowledge base update, capturing the message text, requester, and channel context.",
          setup:
            "Configure Slack event subscriptions for reaction_added. Filter for :book: or :pencil: emojis in #support, #product, or #engineering channels.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Extract article reference and requested change",
        280,
        "You are a knowledge base editor. Extract the target article title or URL, the specific change requested (add/update/correct/remove), the reason for the change, suggested new content if provided, and priority. Return article_ref, change_type, reason, suggested_content, priority, requester_name.",
        "Parse this Slack KB update request: {webhook_payload}",
        [
          { field: "article_ref", as: "article_reference", type: "string" },
          { field: "change_type", as: "change_type", type: "string" },
          { field: "reason", as: "change_reason", type: "string" },
          {
            field: "suggested_content",
            as: "suggested_content",
            type: "string",
          },
          { field: "priority", as: "update_priority", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
        ],
      ),
      n(
        "3",
        "runCode",
        "Check if article exists and determine update scope",
        520,
        {
          runCodeDetails: {
            code: "const isNew = inputs.change_type.toLowerCase() === 'add' || inputs.change_type.toLowerCase() === 'create'; const isBreaking = inputs.change_type.toLowerCase() === 'remove' || inputs.change_type.toLowerCase() === 'correct'; const needsApproval = isBreaking || inputs.update_priority === 'high'; output = { is_new_article: isNew, is_breaking_change: isBreaking, needs_approval: needsApproval, task_type: isNew ? 'Create Article' : 'Update Article' };",
            outputs: [
              { name: "is_new_article", type: "boolean" },
              { name: "is_breaking_change", type: "boolean" },
              { name: "needs_approval", type: "boolean" },
              { name: "task_type", type: "string" },
            ],
          },
        },
      ),
      n(
        "4",
        "hitApi",
        "Confluence: Create update task in KB maintenance space",
        760,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{confluence_domain}.atlassian.net/wiki/rest/api/content",
            headers: [
              { key: "Authorization", value: "Basic {confluence_auth}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "type", value: "page" },
              { key: "title", value: "{task_type}: {article_reference}" },
              { key: "space", value: '{"key":"{kb_tasks_space_key}"}' },
              {
                key: "body",
                value:
                  '{"storage":{"value":"<h2>KB Update Request</h2><p><strong>Article:</strong> {article_reference}</p><p><strong>Change Type:</strong> {change_type}</p><p><strong>Reason:</strong> {change_reason}</p><p><strong>Suggested Content:</strong> {suggested_content}</p><p><strong>Requester:</strong> {requester_name}</p><p><strong>Priority:</strong> {update_priority}</p>","representation":"storage"}}',
              },
            ],
            variables: [
              { name: "id", customName: "confluence_task_id", type: "string" },
              {
                name: "_links.webui",
                customName: "confluence_task_url",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "5",
        "condition",
        "Check: Is this a breaking change requiring approval?",
        1000,
        {
          conditionDetails: {
            conditions: [
              { variable: "{needs_approval}", operator: "==", value: "true" },
            ],
          },
        },
      ),
      n(
        "6",
        "approvalGate",
        "KB Owner: Approve breaking change to knowledge base",
        1240,
        {
          approvalGateDetails: {
            approvers: ["{kb_owner_email}"],
            message:
              "A breaking KB change has been requested:\n\nArticle: {article_reference}\nChange: {change_type}\nReason: {change_reason}\nRequester: {requester_name}\n\nPlease review before this change is implemented.",
          },
        },
      ),
      slackNode(
        "7",
        "Slack: Confirm KB update request received and tracked",
        1480,
        ":white_check_mark: *KB Update Request Logged*\n*Article:* {article_reference}\n*Change:* {change_type} ({update_priority} priority)\n*Reason:* {change_reason}\n*Requester:* {requester_name}\n*Approval needed:* {needs_approval}\n\nConfluence task: {confluence_task_url}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log KB update request for metrics",
        1720,
        "KBUpdates",
        '[["{today_date}","{article_reference}","{change_type}","{change_reason}","{update_priority}","{requester_name}","{needs_approval}","{confluence_task_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Captures KB update requests from Slack emoji reactions, uses AI to extract the article reference and change details, creates a Confluence maintenance task, gates breaking changes behind KB owner approval, confirms the request in Slack, and logs to Google Sheets for tracking update volume.",
  },

  // ─── 163. Event Moderation Helper ─────────────────────────────────────

  "Event Moderation Helper": {
    nodes: [
      trigger(
        "Webhook: Q&A question submitted during live event",
        "webhook_app",
        {
          source: "event_platform",
          event: "question.submitted",
          howItStarts:
            "Fires when an attendee submits a question through the event Q&A feature, providing the question text, submitter name, session, and event details.",
          setup:
            "Configure your event platform (Hopin, Zoom Webinar, or custom) to send webhooks on Q&A submissions. Include question text, submitter, and session metadata.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Classify question appropriateness and topic",
        280,
        "You are an event moderation assistant. Classify the submitted question for appropriateness (appropriate/borderline/inappropriate), topic relevance (on-topic/off-topic/tangential), question quality (insightful/standard/low-effort), and detect any harmful content. Return question_text, appropriateness, relevance, quality, is_harmful, topic_category, cleaned_question.",
        "Moderate this event Q&A submission: {webhook_payload}",
        [
          { field: "question_text", as: "original_question", type: "string" },
          { field: "appropriateness", as: "appropriateness", type: "string" },
          { field: "relevance", as: "relevance", type: "string" },
          { field: "quality", as: "question_quality", type: "string" },
          { field: "is_harmful", as: "is_harmful", type: "boolean" },
          { field: "topic_category", as: "topic_category", type: "string" },
          { field: "cleaned_question", as: "cleaned_question", type: "string" },
        ],
      ),
      n(
        "3",
        "runCode",
        "Check against blocked terms and moderation rules",
        520,
        {
          runCodeDetails: {
            code: "const isAppropriate = inputs.appropriateness === 'appropriate' && !inputs.is_harmful; const isRelevant = inputs.relevance !== 'off-topic'; const passesModeration = isAppropriate && isRelevant; const reason = !isAppropriate ? 'inappropriate content' : !isRelevant ? 'off-topic' : 'passed'; output = { passes_moderation: passesModeration, moderation_reason: reason, show_to_moderator: isAppropriate, auto_reject: inputs.is_harmful };",
            outputs: [
              { name: "passes_moderation", type: "boolean" },
              { name: "moderation_reason", type: "string" },
              { name: "show_to_moderator", type: "boolean" },
              { name: "auto_reject", type: "boolean" },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Check: Does question pass automated moderation?",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "{passes_moderation}",
                operator: "==",
                value: "true",
              },
            ],
          },
        },
      ),
      slackNode(
        "5",
        "Slack: Show question to moderator for final approval",
        1000,
        ":question: *Event Q&A — Moderator Review*\n*Question:* {cleaned_question}\n*From:* {submitter_name}\n*Topic:* {topic_category} | *Quality:* {question_quality}\n*Relevance:* {relevance}\n\nReact :white_check_mark: to approve or :x: to reject.",
      ),
      n("6", "condition", "Check: Moderator approved the question?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "{moderator_approved}", operator: "==", value: "true" },
          ],
        },
      }),
      n(
        "7",
        "hitApi",
        "Event Platform: Publish approved question to live Q&A",
        1480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.eventplatform.com/v1/events/{event_id}/questions/{question_id}/publish",
            headers: [
              { key: "Authorization", value: "Bearer {event_api_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "status", value: "published" },
              { key: "cleaned_text", value: "{cleaned_question}" },
            ],
            variables: [
              {
                name: "id",
                customName: "published_question_id",
                type: "string",
              },
            ],
          },
        },
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log moderation decisions for review",
        1720,
        "EventModeration",
        '[["{today_date}","{submitter_name}","{original_question}","{appropriateness}","{relevance}","{passes_moderation}","{moderation_reason}","{topic_category}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("7", "8"),
    ],
    summary:
      "Moderates live event Q&A submissions using AI to classify appropriateness, relevance, and quality, auto-filters harmful content, routes appropriate questions to a Slack-based moderator review, publishes approved questions to the event platform, and logs all moderation decisions.",
  },

  // ─── 164. Badge Creation from Attendee Form ───────────────────────────

  "Badge Creation from Attendee Form": {
    nodes: [
      trigger(
        "Webhook form: Attendee registration form submitted",
        "webhook_form",
        {
          howItStarts:
            "Fires when a new attendee registration form is submitted, providing the attendee name, role/title, company, email, badge type (speaker/VIP/attendee/staff), and dietary preferences.",
          setup:
            "Connect the event registration form to send webhooks on submission. Include fields for full name, title, company, badge type, and optional photo URL.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Extract and format badge information",
        280,
        "You are an event badge designer. Extract and format the attendee's display name (first name + last initial for privacy, or full name for speakers/VIPs), title, company, badge type, and any special designations. Ensure proper capitalization and formatting. Return display_name, title, company, badge_type, designation, formatted_line1, formatted_line2.",
        "Format badge details from this registration: {webhook_payload}",
        [
          { field: "display_name", as: "display_name", type: "string" },
          { field: "title", as: "attendee_title", type: "string" },
          { field: "company", as: "company_name", type: "string" },
          { field: "badge_type", as: "badge_type", type: "string" },
          { field: "formatted_line1", as: "badge_line1", type: "string" },
          { field: "formatted_line2", as: "badge_line2", type: "string" },
        ],
      ),
      n(
        "3",
        "runCode",
        "Generate badge layout with type-specific styling",
        520,
        {
          runCodeDetails: {
            code: "const typeColors = { 'speaker': '#FF6B35', 'vip': '#FFD700', 'attendee': '#4A90D9', 'staff': '#2ECC71', 'sponsor': '#9B59B6' }; const type = inputs.badge_type.toLowerCase(); const color = typeColors[type] || typeColors.attendee; const badgeId = 'BADGE-' + Date.now().toString(36).toUpperCase(); output = { badge_color: color, badge_id: badgeId, layout_template: type + '_badge_template', is_vip: type === 'vip' || type === 'speaker' };",
            outputs: [
              { name: "badge_color", type: "string" },
              { name: "badge_id", type: "string" },
              { name: "layout_template", type: "string" },
              { name: "is_vip", type: "boolean" },
            ],
          },
        },
      ),
      n(
        "4",
        "hitApi",
        "Google Drive: Save generated badge PDF to event folder",
        760,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "name", value: "{badge_id}_{display_name}_badge.pdf" },
              { key: "parents", value: '["{badges_folder_id}"]' },
              { key: "mimeType", value: "application/pdf" },
            ],
            variables: [
              { name: "id", customName: "badge_file_id", type: "string" },
              {
                name: "webViewLink",
                customName: "badge_file_url",
                type: "string",
              },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Email: Send badge to print queue",
        1000,
        "{print_queue_email}",
        "Badge Ready for Print: {badge_id} — {display_name} ({badge_type})",
        "New badge ready for printing:\n\nBadge ID: {badge_id}\nName: {display_name}\nTitle: {attendee_title}\nCompany: {company_name}\nBadge Type: {badge_type}\nColor Code: {badge_color}\n\nBadge file: {badge_file_url}\n\nLine 1: {badge_line1}\nLine 2: {badge_line2}",
      ),
      slackNode(
        "6",
        "Slack: Confirm badge created in #event-ops channel",
        1240,
        ":id: *Badge Created*\nID: {badge_id}\nName: {display_name} | {attendee_title}, {company_name}\nType: {badge_type} ({badge_color})\nSent to print queue.",
      ),
      n("7", "hitApi", "Airtable: Log attendee badge record", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.airtable.com/v0/{airtable_base_id}/EventBadges",
          headers: [
            { key: "Authorization", value: "Bearer {airtable_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "records",
              value:
                '[{"fields":{"Badge ID":"{badge_id}","Name":"{display_name}","Title":"{attendee_title}","Company":"{company_name}","Badge Type":"{badge_type}","File URL":"{badge_file_url}","Date":"{today_date}"}}]',
            },
          ],
          variables: [
            {
              name: "records[0].id",
              customName: "airtable_badge_id",
              type: "string",
            },
          ],
        },
      }),
      sheetsNode(
        "8",
        "Google Sheets: Update badge count and type breakdown",
        1720,
        "BadgeCount",
        '[["{today_date}","{badge_id}","{display_name}","{badge_type}","{company_name}","{badge_file_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Processes attendee registration forms, uses AI to format badge display names and titles, generates type-specific badge layouts with color coding, saves the badge PDF to Google Drive, sends to the print queue via email, confirms in Slack, logs to Airtable, and tracks badge counts in Google Sheets.",
  },

  // ─── 165. Task Reprioritization Digest ────────────────────────────────

  "Task Reprioritization Digest": {
    nodes: [
      trigger("Schedule: Daily task priority review at 8 AM", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts:
          "Fires daily at 8 AM to fetch all open Asana tasks, analyze priorities using AI, compare against current assignments, and send a reprioritization digest to team leads.",
        setup:
          "Configure the Asana project IDs to analyze. Set the AI prioritization criteria (urgency, impact, dependencies, deadlines).",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "Asana: Fetch all open tasks across tracked projects",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://app.asana.com/api/1.0/tasks?project={project_id}&completed=false&opt_fields=name,assignee.name,due_on,custom_fields,tags,notes,created_at",
            headers: [{ key: "Authorization", value: "Bearer {asana_token}" }],
            body: [],
            variables: [
              { name: "data", customName: "open_tasks", type: "object" },
              { name: "data.length", customName: "task_count", type: "number" },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "OpenAI: Recommend priority changes based on context",
        520,
        "You are a project prioritization expert. Analyze the open tasks considering deadlines, dependencies, assignee workload, task age, and business impact. Recommend priority changes for tasks that appear mis-prioritized. For each recommended change, explain the rationale. Return recommended_changes (array with task_name, current_priority, recommended_priority, rationale), change_count, overall_assessment.",
        "Analyze these open tasks and recommend priority adjustments: {open_tasks}. Today's date: {today_date}",
        [
          {
            field: "recommended_changes",
            as: "priority_changes_json",
            type: "string",
          },
          { field: "change_count", as: "change_count", type: "number" },
          {
            field: "overall_assessment",
            as: "overall_assessment",
            type: "string",
          },
        ],
      ),
      n(
        "4",
        "runCode",
        "Compare current vs recommended and format digest",
        760,
        {
          runCodeDetails: {
            code: "const changes = JSON.parse(inputs.priority_changes_json || '[]'); const hasChanges = changes.length > 0; const digestLines = changes.map(c => '- ' + c.task_name + ': ' + c.current_priority + ' -> ' + c.recommended_priority + ' (' + c.rationale + ')'); output = { has_changes: hasChanges, digest_text: digestLines.join('\\n'), change_summary: hasChanges ? changes.length + ' tasks need reprioritization' : 'All priorities look correct', urgent_changes: changes.filter(c => c.recommended_priority === 'high' || c.recommended_priority === 'urgent').length };",
            outputs: [
              { name: "has_changes", type: "boolean" },
              { name: "digest_text", type: "string" },
              { name: "change_summary", type: "string" },
              { name: "urgent_changes", type: "number" },
            ],
          },
        },
      ),
      n("5", "condition", "Check: Are priority changes recommended?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "{has_changes}", operator: "==", value: "true" },
          ],
        },
      }),
      slackNode(
        "6",
        "Slack: Send reprioritization digest to team leads",
        1240,
        ":bar_chart: *Daily Priority Review — {today_date}*\n\n{overall_assessment}\n\n*Recommended Changes ({change_count}):*\n{digest_text}\n\n*Summary:* {change_summary}\n*Urgent escalations:* {urgent_changes}\n\nPlease review and apply changes in Asana as appropriate.",
      ),
      n("7", "hitApi", "Asana: Apply approved priority updates in bulk", 1480, {
        hitApiDetails: {
          method: "PUT",
          url: "https://app.asana.com/api/1.0/tasks/{task_id}",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"custom_fields":{"{priority_field_id}":"{new_priority_value}"}}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "updated_task_id", type: "string" },
          ],
        },
      }),
      sheetsNode(
        "8",
        "Google Sheets: Log daily priority review outcomes",
        1720,
        "PriorityReview",
        '[["{today_date}","{task_count}","{change_count}","{urgent_changes}","{overall_assessment}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Performs a daily AI-powered priority review of all open Asana tasks, identifies mis-prioritized items with rationale, sends a digest to team leads via Slack with recommended changes, applies approved priority updates in Asana, and logs review outcomes to Google Sheets for tracking decision patterns.",
  },
};
